import { Alert, Badge, Button, Code, Stack, Text } from '@mantine/core';
import type { Preview } from '@/types/executionCompositions.ts';
import { ExactDetails, LocationLabel } from './CompositionPresentation.tsx';
import { locationKey } from './compositionPresentation.ts';

export default function ReleaseSelectionReview({
  preview,
  canSave,
  expired,
  onSave,
  pendingRequestId,
  onRecover,
  recoverDisabled,
}: {
  preview: Preview | null;
  canSave: boolean;
  expired: boolean;
  onSave: () => void;
  pendingRequestId?: string;
  onRecover: () => void;
  recoverDisabled: boolean;
}) {
  return (
    <>
      {preview && (
        <Stack>
          <Badge>{preview.intent.status}</Badge>
          <Text>Preview expires {new Date(preview.intent.expires_at).toLocaleString()}</Text>
          <Text fw={600}>Proposed release selection — not saved</Text>
          {preview.intent.candidate_identity && (
            <ExactDetails
              summary="Proposed candidate identities"
              values={{
                'Execution hash': preview.intent.candidate_identity.composition_digest,
                'Resource hash': preview.intent.candidate_identity.resource_digest,
                'Documentation hash': preview.intent.candidate_identity.documentation_digest,
                'Requirements hash': preview.intent.candidate_identity.requirements_digest,
              }}
            />
          )}
          {preview.intent.findings.map((finding, index) => (
            <Alert key={index} color={finding.blocking ? 'orange' : 'blue'}>
              {finding.message}
              <Text size="sm">Next: {finding.next_action}</Text>
            </Alert>
          ))}
          <Text fw={600}>Changed dependencies</Text>
          {preview.intent.changed_locations.length ? (
            preview.intent.changed_locations.map((location) => (
              <Text key={locationKey(location)}>
                <LocationLabel location={location} />
              </Text>
            ))
          ) : (
            <Text size="sm">No dependency changes.</Text>
          )}
          <Text>
            {preview.intent.unaffected_roots.length} unchanged release trigger
            {preview.intent.unaffected_roots.length === 1 ? '' : 's'} remain included. Resources and
            proof may still need requalification.
          </Text>
          <Text>
            {preview.intent.proposed_version_edits.length} implementation edit
            {preview.intent.proposed_version_edits.length === 1 ? '' : 's'} will create a new draft
            version when saved.
          </Text>
          {preview.intent.consumer_impacts.map((impact, index) => (
            <Alert key={index} color={impact.status === 'compatible' ? 'blue' : 'orange'}>
              Consumer compatibility: {impact.status}
              <Text>{impact.explanation}</Text>
            </Alert>
          ))}
          <ExactDetails
            summary="Technical preview details"
            values={{
              'Changed location count': String(preview.intent.changed_locations.length),
              'Unaffected trigger count': String(preview.intent.unaffected_roots.length),
              'Implementation edit count': String(preview.intent.proposed_version_edits.length),
            }}
          />
          <Text>
            Recommended test evidence:{' '}
            {preview.intent.required_tests
              .map((test) => `${test.kind}: ${test.evidence.case_id} (${test.target.binding_id})`)
              .join(', ') || 'none declared'}
          </Text>
          <Button disabled={!canSave} onClick={onSave}>
            Save release configuration
          </Button>
          {expired && <Text c="orange">Preview expired. Prepare and review again.</Text>}
        </Stack>
      )}
      {pendingRequestId && (
        <Alert color="orange">
          Save is unconfirmed. Request <Code>{pendingRequestId}</Code>
          <Button disabled={recoverDisabled} onClick={onRecover}>
            Recover same save request
          </Button>
        </Alert>
      )}
    </>
  );
}
